import { AppError } from "@hisani/core";
import { z } from "zod";
export const AUTH_ERROR_CODES = {
    accountExists: "AUTH_ACCOUNT_EXISTS",
    confirmationRequired: "AUTH_CONFIRMATION_REQUIRED",
    contactPointConflict: "AUTH_CONTACT_POINT_CONFLICT",
    forbidden: "AUTH_FORBIDDEN",
    identityUnverified: "AUTH_IDENTITY_UNVERIFIED",
    identitySyncUnavailable: "AUTH_IDENTITY_SYNC_UNAVAILABLE",
    invalidCode: "AUTH_INVALID_CODE",
    invalidCredentials: "AUTH_INVALID_CREDENTIALS",
    invalidRequest: "AUTH_INVALID_REQUEST",
    invalidTransaction: "AUTH_INVALID_TRANSACTION",
    mfaRequired: "AUTH_MFA_REQUIRED",
    passwordRequirements: "AUTH_PASSWORD_REQUIREMENTS",
    passwordResetRequired: "AUTH_PASSWORD_RESET_REQUIRED",
    profilePictureUnavailable: "AUTH_PROFILE_PICTURE_UNAVAILABLE",
    profileIncomplete: "AUTH_PROFILE_INCOMPLETE",
    providerUnavailable: "AUTH_PROVIDER_UNAVAILABLE",
    rateLimited: "AUTH_RATE_LIMITED",
    roleForbidden: "AUTH_ROLE_FORBIDDEN",
    unauthenticated: "AUTH_UNAUTHENTICATED",
    unsupportedChallenge: "AUTH_UNSUPPORTED_CHALLENGE",
};
export const CognitoUsernameSchema = z
    .string()
    .min(1)
    .max(128)
    .refine((value) => value === value.trim() && !/[\u0000-\u001f\u007f]/u.test(value));
export const PRIMARY_ROLES = [
    "OWNER",
    "TRAINER",
    "ASSISTANT_TRAINER",
    "RACING_MANAGER",
    "VET",
    "FARRIER",
    "RIDER",
    "GROOM",
    "VENDOR",
    "HEAD_LAD",
    "FEEDMAN",
    "TRAVELLING_LAD",
    "TRAVELLING_HEAD_LAD",
];
export const PrimaryRoleSchema = z.enum(PRIMARY_ROLES);
export const ContactPointTypeSchema = z.enum(["EMAIL", "PHONE"]);
export const PlatformRoleSchema = z.enum(["HORSEMAN", "ADMIN"]);
export const UserSubscriptionTierSchema = z.enum(["FREE", "PRO"]);
export const AccountStatusSchema = z.enum(["ACTIVE", "SUSPENDED"]);
const beginAuthenticationSchema = z.object({
    mode: z.enum(["sign-in", "sign-up"]),
    locale: z.enum(["en", "ar"]).default("en"),
    returnTo: z.string().max(2_048).optional(),
});
const callbackSchema = z.object({
    code: z.string().min(1).max(8_192),
    state: z.string().min(16).max(1_024),
    transactionCookie: z.string().min(1).max(16_384),
});
const transactionSchema = z.object({
    state: z.string().min(16),
    codeVerifier: z.string().min(43).max(128),
    mode: z.enum(["sign-in", "sign-up"]),
    locale: z.enum(["en", "ar"]).default("en"),
    returnTo: z.string(),
    expiresAtEpochMilliseconds: z.number().int().positive(),
});
const completeProfileSchema = z.object({
    displayName: z.string().trim().min(2).max(100),
    primaryRole: PrimaryRoleSchema,
    discoverable: z.boolean(),
});
const emailSchema = z
    .string()
    .trim()
    .email()
    .max(320)
    .transform((value) => value.toLowerCase());
const phoneSchema = z
    .string()
    .trim()
    .regex(/^\+[1-9]\d{7,14}$/);
const contactPointInputSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("EMAIL"), value: emailSchema }),
    z.object({ type: z.literal("PHONE"), value: phoneSchema }),
]);
const passwordSchema = z.string().min(12).max(256);
const codeSchema = z
    .string()
    .trim()
    .regex(/^\d{6}$/);
const passwordAuthenticationSchema = z.object({
    email: emailSchema,
    password: passwordSchema,
    locale: z.enum(["en", "ar"]).default("en"),
    returnTo: z.string().max(2_048).optional(),
});
const signUpSchema = z.object({ contactPoint: contactPointInputSchema });
const confirmationSchema = z.object({
    contactPoint: contactPointInputSchema,
    code: codeSchema,
    locale: z.enum(["en", "ar"]).default("en"),
    returnTo: z.string().max(2_048).optional(),
});
const otpStartSchema = z.object({
    contactPoint: contactPointInputSchema,
    locale: z.enum(["en", "ar"]).default("en"),
    returnTo: z.string().max(2_048).optional(),
});
const resetConfirmationSchema = z.object({
    email: emailSchema,
    code: codeSchema,
    password: passwordSchema,
});
const mfaSchema = z.object({
    code: codeSchema,
    challengeCookie: z.string().min(1).max(16_384),
});
const mfaTransactionSchema = z.object({
    challenge: z.enum(["SOFTWARE_TOKEN_MFA", "SMS_MFA"]),
    session: z.string().min(1).max(16_384),
    username: z.string().min(1).max(320),
    locale: z.enum(["en", "ar"]),
    returnTo: z.string(),
    expiresAtEpochMilliseconds: z.number().int().positive(),
});
const otpTransactionSchema = z.object({
    challenge: z.enum(["EMAIL_OTP", "SMS_OTP"]),
    session: z.string().min(1).max(16_384),
    username: z.string().min(1).max(320),
    locale: z.enum(["en", "ar"]),
    returnTo: z.string(),
    expiresAtEpochMilliseconds: z.number().int().positive(),
});
const contactPointVerificationTransactionSchema = z.object({
    type: ContactPointTypeSchema,
    normalizedValue: z.string().min(3).max(320),
    username: CognitoUsernameSchema,
    expiresAtEpochMilliseconds: z.number().int().positive(),
});
const mfaSetupTransactionSchema = z.object({
    username: z.string().min(1).max(320),
    expiresAtEpochMilliseconds: z.number().int().positive(),
});
const mfaSetupVerificationSchema = z.object({
    code: codeSchema,
    setupCookie: z.string().min(1).max(16_384),
});
const TRANSACTION_LIFETIME_MILLISECONDS = 10 * 60 * 1_000;
const SESSION_LIFETIME_MILLISECONDS = 30 * 24 * 60 * 60 * 1_000;
export async function beginAuthentication(input, dependencies) {
    const parsed = beginAuthenticationSchema.safeParse(input);
    if (!parsed.success) {
        throw invalidRequest(parsed.error.flatten().fieldErrors);
    }
    const state = dependencies.random.randomUrlSafe(32);
    const codeVerifier = dependencies.random.randomUrlSafe(64);
    const codeChallenge = dependencies.hashing.sha256(codeVerifier);
    const transaction = {
        state,
        codeVerifier,
        mode: parsed.data.mode,
        locale: parsed.data.locale,
        returnTo: normalizeReturnPath(parsed.data.returnTo),
        expiresAtEpochMilliseconds: dependencies.clock.now().valueOf() + TRANSACTION_LIFETIME_MILLISECONDS,
    };
    const transactionCookie = await dependencies.secrets.seal(JSON.stringify(transaction));
    return {
        authorizationUrl: dependencies.provider.createAuthorizationUrl({
            mode: transaction.mode,
            state,
            codeChallenge,
        }),
        transactionCookie,
    };
}
export async function completeAuthentication(input, dependencies) {
    const parsed = callbackSchema.safeParse(input);
    if (!parsed.success) {
        throw invalidRequest(parsed.error.flatten().fieldErrors);
    }
    const transaction = await openTransactionPayload(parsed.data.transactionCookie, dependencies, transactionSchema);
    if (!dependencies.hashing.constantTimeEqual(transaction.state, parsed.data.state) ||
        transaction.expiresAtEpochMilliseconds < dependencies.clock.now().valueOf()) {
        throw new AppError({
            code: AUTH_ERROR_CODES.invalidTransaction,
            message: "The authentication request is invalid or has expired.",
        });
    }
    const identity = await dependencies.provider.exchangeAuthorizationCode({
        code: parsed.data.code,
        codeVerifier: transaction.codeVerifier,
    });
    return createApplicationSession(identity, transaction.locale, transaction.returnTo, dependencies);
}
export async function signInWithPassword(input, dependencies) {
    const parsed = passwordAuthenticationSchema.safeParse(input);
    if (!parsed.success)
        throw invalidRequest(parsed.error.flatten().fieldErrors);
    const result = await dependencies.provider.signInWithPassword({
        username: parsed.data.email,
        password: parsed.data.password,
    });
    const returnTo = normalizeReturnPath(parsed.data.returnTo);
    if (result.status === "AUTHENTICATED") {
        return {
            status: "AUTHENTICATED",
            ...(await createApplicationSession(result.identity, parsed.data.locale, returnTo, dependencies)),
        };
    }
    return {
        status: "MFA_REQUIRED",
        challengeCookie: await dependencies.secrets.seal(JSON.stringify({
            challenge: result.challenge,
            session: result.session,
            username: result.username,
            locale: parsed.data.locale,
            returnTo,
            expiresAtEpochMilliseconds: dependencies.clock.now().valueOf() +
                TRANSACTION_LIFETIME_MILLISECONDS,
        })),
    };
}
export async function completeMfaChallenge(input, dependencies) {
    const parsed = mfaSchema.safeParse(input);
    if (!parsed.success)
        throw invalidRequest(parsed.error.flatten().fieldErrors);
    const transaction = await openTransactionPayload(parsed.data.challengeCookie, dependencies, mfaTransactionSchema);
    if (transaction.expiresAtEpochMilliseconds < dependencies.clock.now().valueOf()) {
        throw new AppError({
            code: AUTH_ERROR_CODES.invalidTransaction,
            message: "The authentication request is invalid or has expired.",
        });
    }
    const identity = await dependencies.provider.respondToMfa({
        challenge: transaction.challenge,
        code: parsed.data.code,
        session: transaction.session,
        username: transaction.username,
    });
    return createApplicationSession(identity, transaction.locale, transaction.returnTo, dependencies);
}
export async function startOtpSignIn(input, dependencies) {
    const parsed = otpStartSchema.safeParse(input);
    if (!parsed.success)
        throw invalidRequest(parsed.error.flatten().fieldErrors);
    const contactPoint = normalizedContactPoint(parsed.data.contactPoint);
    const challenge = await dependencies.provider.startOtpSignIn(contactPoint);
    const expectedChallenge = contactPoint.type === "EMAIL" ? "EMAIL_OTP" : "SMS_OTP";
    if (challenge.challenge !== expectedChallenge) {
        throw new AppError({
            code: AUTH_ERROR_CODES.unsupportedChallenge,
            message: "The identity provider returned an unsupported challenge.",
        });
    }
    return {
        delivery: contactPoint.type,
        challengeCookie: await dependencies.secrets.seal(JSON.stringify({
            ...challenge,
            locale: parsed.data.locale,
            returnTo: normalizeReturnPath(parsed.data.returnTo),
            expiresAtEpochMilliseconds: dependencies.clock.now().valueOf() +
                TRANSACTION_LIFETIME_MILLISECONDS,
        })),
    };
}
export async function completeOtpSignIn(input, dependencies) {
    const parsed = mfaSchema.safeParse(input);
    if (!parsed.success)
        throw invalidRequest(parsed.error.flatten().fieldErrors);
    const transaction = await openTransactionPayload(parsed.data.challengeCookie, dependencies, otpTransactionSchema);
    if (transaction.expiresAtEpochMilliseconds < dependencies.clock.now().valueOf()) {
        throw new AppError({
            code: AUTH_ERROR_CODES.invalidTransaction,
            message: "The authentication request is invalid or has expired.",
        });
    }
    const identity = await dependencies.provider.respondToOtp({
        challenge: transaction.challenge,
        session: transaction.session,
        username: transaction.username,
        code: parsed.data.code,
    });
    return createApplicationSession(identity, transaction.locale, transaction.returnTo, dependencies);
}
export async function readMfaState(input, dependencies) {
    const principal = await authenticateSession(input.sessionToken, dependencies);
    const providerStatus = await dependencies.provider.assertUserActive(principal.account.cognitoUsername);
    return { mfaEnabled: providerStatus.mfaEnabled };
}
export async function startMfaSetup(input, dependencies) {
    const session = await openSession(input.sessionToken, dependencies);
    await assertMfaChangeAllowed(session, dependencies);
    const refreshToken = await dependencies.secrets.open(session.encryptedRefreshToken);
    const setup = await dependencies.provider.startSoftwareTokenMfaSetup({
        username: session.account.cognitoUsername,
        refreshToken,
    });
    const setupCookie = await dependencies.secrets.seal(JSON.stringify({
        username: session.account.cognitoUsername,
        expiresAtEpochMilliseconds: dependencies.clock.now().valueOf() + TRANSACTION_LIFETIME_MILLISECONDS,
    }));
    return { ...setup, setupCookie };
}
export async function completeMfaSetup(input, dependencies) {
    const parsed = mfaSetupVerificationSchema.safeParse({
        code: input.code,
        setupCookie: input.setupCookie,
    });
    if (!parsed.success) {
        throw invalidRequest(parsed.error.flatten().fieldErrors);
    }
    const transaction = await openTransactionPayload(parsed.data.setupCookie, dependencies, mfaSetupTransactionSchema);
    const session = await openSession(input.sessionToken, dependencies);
    if (transaction.username !== session.account.cognitoUsername ||
        transaction.expiresAtEpochMilliseconds < dependencies.clock.now().valueOf()) {
        throw new AppError({
            code: AUTH_ERROR_CODES.invalidTransaction,
            message: "The authentication request is invalid or has expired.",
        });
    }
    await assertMfaChangeAllowed(session, dependencies);
    const refreshToken = await dependencies.secrets.open(session.encryptedRefreshToken);
    await dependencies.provider.completeSoftwareTokenMfa({
        username: transaction.username,
        code: parsed.data.code,
        refreshToken,
    });
    // Enrolment proves possession of the new factor, so this session satisfies
    // the MFA policy without forcing a sign-out and sign-in round trip.
    await dependencies.sessions.markMfaAuthenticated(session.id, dependencies.clock.now());
}
export async function disableMfa(input, dependencies) {
    const session = await openSession(input.sessionToken, dependencies);
    await assertMfaChangeAllowed(session, dependencies);
    const refreshToken = await dependencies.secrets.open(session.encryptedRefreshToken);
    await dependencies.sessions.clearMfaAuthentication(session.horsemanId, dependencies.clock.now());
    await dependencies.provider.disableSoftwareTokenMfa({
        username: session.account.cognitoUsername,
        refreshToken,
    });
}
export async function signUp(input, dependencies) {
    const parsed = signUpSchema.safeParse(input);
    if (!parsed.success)
        throw invalidRequest(parsed.error.flatten().fieldErrors);
    return dependencies.provider.signUp({
        contactPoint: normalizedContactPoint(parsed.data.contactPoint),
    });
}
export async function confirmSignUp(input, dependencies) {
    const parsed = confirmationSchema.safeParse(input);
    if (!parsed.success)
        throw invalidRequest(parsed.error.flatten().fieldErrors);
    const identity = await dependencies.provider.confirmSignUp({
        contactPoint: normalizedContactPoint(parsed.data.contactPoint),
        code: parsed.data.code,
    });
    return createApplicationSession(identity, parsed.data.locale, normalizeReturnPath(parsed.data.returnTo), dependencies);
}
export async function resendSignUpCode(contactPoint, dependencies) {
    const parsed = contactPointInputSchema.safeParse(contactPoint);
    if (!parsed.success)
        throw invalidRequest(parsed.error.flatten().fieldErrors);
    return dependencies.provider.resendSignUpCode(normalizedContactPoint(parsed.data));
}
export async function startContactPointVerification(input, dependencies) {
    const parsed = contactPointInputSchema.safeParse(input.contactPoint);
    if (!parsed.success)
        throw invalidRequest(parsed.error.flatten().fieldErrors);
    const session = await openSession(input.sessionToken, dependencies);
    const contactPoint = normalizedContactPoint(parsed.data);
    if (session.account.verifiedContactPoints.some((existing) => existing.type === contactPoint.type)) {
        throw new AppError({
            code: AUTH_ERROR_CODES.contactPointConflict,
            message: "This account already has that type of verified Contact Point.",
        });
    }
    const refreshToken = await dependencies.secrets.open(session.encryptedRefreshToken);
    const result = await dependencies.provider.startContactPointVerification({
        username: session.account.cognitoUsername,
        refreshToken,
        contactPoint,
    });
    return {
        delivery: result.delivery,
        verificationCookie: await dependencies.secrets.seal(JSON.stringify({
            type: contactPoint.type,
            normalizedValue: contactPoint.normalizedValue,
            username: session.account.cognitoUsername,
            expiresAtEpochMilliseconds: dependencies.clock.now().valueOf() +
                TRANSACTION_LIFETIME_MILLISECONDS,
        })),
    };
}
export async function completeContactPointVerification(input, dependencies) {
    const code = codeSchema.safeParse(input.code);
    if (!code.success) {
        throw invalidRequest({
            code: code.error.issues.map((issue) => issue.message),
        });
    }
    const transaction = await openTransactionPayload(input.verificationCookie, dependencies, contactPointVerificationTransactionSchema);
    const session = await openSession(input.sessionToken, dependencies);
    if (transaction.username !== session.account.cognitoUsername ||
        transaction.expiresAtEpochMilliseconds < dependencies.clock.now().valueOf()) {
        throw new AppError({
            code: AUTH_ERROR_CODES.invalidTransaction,
            message: "The Contact Point verification request is invalid or has expired.",
        });
    }
    const refreshToken = await dependencies.secrets.open(session.encryptedRefreshToken);
    const identity = await dependencies.provider.confirmContactPoint({
        username: transaction.username,
        refreshToken,
        type: transaction.type,
        code: code.data,
    });
    if (!identity.verifiedContactPoints.some((contactPoint) => contactPoint.type === transaction.type &&
        contactPoint.normalizedValue === transaction.normalizedValue)) {
        throw new AppError({
            code: AUTH_ERROR_CODES.identityUnverified,
            message: "The Contact Point has not been verified.",
        });
    }
    return dependencies.identities.upsertVerifiedIdentity(identity);
}
export async function requestPasswordReset(email, dependencies) {
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success)
        throw invalidRequest({
            email: parsed.error.issues.map((issue) => issue.message),
        });
    return dependencies.provider.requestPasswordReset(parsed.data);
}
export async function confirmPasswordReset(input, dependencies) {
    const parsed = resetConfirmationSchema.safeParse(input);
    if (!parsed.success)
        throw invalidRequest(parsed.error.flatten().fieldErrors);
    await dependencies.provider.confirmPasswordReset(parsed.data);
}
async function createApplicationSession(identity, locale, returnTo, dependencies) {
    if (identity.verifiedContactPoints.length === 0) {
        throw new AppError({
            code: AUTH_ERROR_CODES.identityUnverified,
            message: "A verified email address or phone number is required.",
        });
    }
    const upsertedAccount = await dependencies.identities.upsertVerifiedIdentity(identity);
    const sessionToken = dependencies.random.randomUrlSafe(48);
    const tokenHash = dependencies.hashing.sha256(sessionToken);
    const encryptedRefreshToken = await dependencies.secrets.seal(identity.refreshToken);
    const expiresAt = new Date(dependencies.clock.now().valueOf() + SESSION_LIFETIME_MILLISECONDS);
    const session = await dependencies.sessions.create({
        horsemanId: upsertedAccount.id,
        tokenHash,
        encryptedRefreshToken,
        mfaAuthenticated: identity.mfaAuthenticated,
        expiresAt,
    });
    if (session.account.accountStatus !== "ACTIVE") {
        throw unauthenticated();
    }
    return {
        sessionToken,
        account: session.account,
        returnTo: session.account.profileCompleted
            ? returnTo
            : `/${locale}/onboarding`,
    };
}
export async function authenticateSession(sessionToken, dependencies) {
    const record = await openSession(sessionToken, dependencies);
    const providerStatus = await dependencies.provider.assertUserActive(record.account.cognitoUsername);
    return {
        sessionId: record.id,
        account: record.account,
        mfaAuthenticated: record.mfaAuthenticated && providerStatus.mfaEnabled,
    };
}
export async function completeHorsemanProfile(principal, input, dependencies) {
    const parsed = completeProfileSchema.safeParse(input);
    if (!parsed.success) {
        throw invalidRequest(parsed.error.flatten().fieldErrors);
    }
    if (principal.account.verifiedContactPoints.length === 0) {
        throw new AppError({
            code: AUTH_ERROR_CODES.identityUnverified,
            message: "A verified email address or phone number is required.",
        });
    }
    return dependencies.identities.completeProfile({
        horsemanId: principal.account.id,
        ...parsed.data,
    });
}
export async function logout(sessionToken, dependencies) {
    if (sessionToken === null || sessionToken.length < 32) {
        return { providerRevocation: "not-applicable" };
    }
    const record = await dependencies.sessions.findByTokenHash(dependencies.hashing.sha256(sessionToken));
    if (record === null || record.revokedAt !== null) {
        return { providerRevocation: "not-applicable" };
    }
    await dependencies.sessions.revoke(record.id, dependencies.clock.now());
    try {
        const refreshToken = await dependencies.secrets.open(record.encryptedRefreshToken);
        await dependencies.provider.revokeRefreshToken(refreshToken);
        return { providerRevocation: "completed" };
    }
    catch {
        return { providerRevocation: "failed" };
    }
}
export function requireCompletedProfile(principal) {
    if (!principal.account.profileCompleted) {
        throw new AppError({
            code: AUTH_ERROR_CODES.profileIncomplete,
            message: "Complete the Horseman profile before continuing.",
        });
    }
    return principal;
}
export function requireAdmin(principal) {
    requireCompletedProfile(principal);
    if (principal.account.platformRole !== "ADMIN") {
        throw new AppError({
            code: AUTH_ERROR_CODES.roleForbidden,
            message: "This account does not have Admin access.",
        });
    }
    if (!principal.mfaAuthenticated) {
        throw new AppError({
            code: AUTH_ERROR_CODES.mfaRequired,
            message: "Sign in with MFA before using Admin.",
        });
    }
    return principal;
}
function normalizeReturnPath(value) {
    if (value === undefined ||
        !value.startsWith("/") ||
        value.startsWith("//") ||
        value.includes("\\") ||
        /[\u0000-\u001f\u007f]/u.test(value)) {
        return "/";
    }
    return value;
}
async function openSession(sessionToken, dependencies) {
    if (sessionToken === null || sessionToken.length < 32) {
        throw unauthenticated();
    }
    const record = await dependencies.sessions.findByTokenHash(dependencies.hashing.sha256(sessionToken));
    const now = dependencies.clock.now();
    if (record === null ||
        record.revokedAt !== null ||
        record.expiresAt.valueOf() <= now.valueOf() ||
        record.account.accountStatus !== "ACTIVE") {
        throw unauthenticated();
    }
    await dependencies.sessions.touch(record.id, now);
    return record;
}
/**
 * Enrolling or removing a factor is as sensitive as authenticating with it, so
 * an account that already has MFA may only change it from a session that itself
 * satisfied an MFA challenge. The first enrolment is exempt because no factor
 * exists yet to step up with.
 */
async function assertMfaChangeAllowed(session, dependencies) {
    const providerStatus = await dependencies.provider.assertUserActive(session.account.cognitoUsername);
    if (providerStatus.mfaEnabled && !session.mfaAuthenticated) {
        throw new AppError({
            code: AUTH_ERROR_CODES.forbidden,
            message: "Sign in with your authenticator before changing MFA settings.",
        });
    }
}
async function openTransactionPayload(payload, dependencies, schema) {
    try {
        const opened = await dependencies.secrets.open(payload);
        return schema.parse(JSON.parse(opened));
    }
    catch (error) {
        throw new AppError({
            code: AUTH_ERROR_CODES.invalidTransaction,
            message: "The authentication request is invalid or has expired.",
            cause: error,
        });
    }
}
function invalidRequest(fieldErrors) {
    return new AppError({
        code: AUTH_ERROR_CODES.invalidRequest,
        message: "The authentication request is invalid.",
        fieldErrors: Object.fromEntries(Object.entries(fieldErrors).filter((entry) => entry[1] !== undefined)),
    });
}
function unauthenticated() {
    return new AppError({
        code: AUTH_ERROR_CODES.unauthenticated,
        message: "Authentication is required.",
    });
}
function normalizedContactPoint(input) {
    return { type: input.type, normalizedValue: input.value };
}
